/**
 * Auto-palette runner. When the user turns on Auto in the palette control, this
 * keeps the chart's day/dusk/night palette in step with the actual sun at the
 * boat's position, so the chart dims through dusk into night and brightens again
 * at dawn without anyone touching the menu on a long passage.
 *
 * How it decides: the sun's live altitude at ownship lat/long (see
 * environment/sun.ts) maps to day above the horizon, dusk in the civil-twilight
 * band either side, night below that.
 *
 * When to recompute: on a timer (once a minute is plenty — the palette only
 * changes a few times a day) and whenever the boat's position moves enough to
 * matter. A small move doesn't shift sunrise, so we only recompute on a position
 * change of about a tenth of a degree (~6 nm) or more, which keeps us off the
 * every-fix churn while still tracking a real passage.
 *
 * No fix yet: we leave the palette exactly where it is rather than guessing, so
 * turning Auto on before the GPS has locked never throws the chart to night in
 * daylight. Renders nothing; mirrors NightRunner.
 */
import { useEffect, useRef } from 'react';
import { useChartPlotterStore } from '@/store';
import { paletteForSun } from '@/environment';

/** How often to re-check the sun (ms). A minute is ample for a palette. */
const RECHECK_MS = 60_000;
/** Only recompute on a position move this large (deg) — ~6 nm. */
const MOVE_DEG = 0.1;

export function AutoPaletteRunner() {
  const paletteAuto = useChartPlotterStore((s) => s.paletteAuto);
  // Track the last position we computed for, so a jittering fix doesn't recompute
  // every tick — only a real move does.
  const lastPos = useRef<[number, number] | null>(null);

  useEffect(() => {
    if (!paletteAuto) {
      lastPos.current = null;
      return;
    }
    const apply = (): void => {
      const st = useChartPlotterStore.getState();
      const pos = st.ownship.position;
      if (!pos) return; // no fix — leave the current palette alone
      const [lon, lat] = pos;
      const next = paletteForSun(lat, lon);
      // setPalette persists it too, so when Auto is later turned off the last
      // auto choice is the one that stays pinned. Don't touch paletteAuto here.
      if (next !== st.palette) st.setPalette(next);
      lastPos.current = pos;
    };

    apply();
    const timer = setInterval(apply, RECHECK_MS);
    // Recompute promptly on a meaningful position change, not just on the timer,
    // so crossing into a new area (or getting the first fix) transitions without
    // waiting up to a minute.
    const unsub = useChartPlotterStore.subscribe((s) => {
      const pos = s.ownship.position;
      if (!pos) return;
      const last = lastPos.current;
      if (!last || Math.abs(pos[0] - last[0]) >= MOVE_DEG || Math.abs(pos[1] - last[1]) >= MOVE_DEG) {
        apply();
      }
    });

    return () => {
      clearInterval(timer);
      unsub();
    };
  }, [paletteAuto]);

  return null;
}
